/**
 * Per-provider imagery keys.
 *
 * Separate from `capture/ApiKeyStore` rather than a second field on it, because these are keys to
 * *different services with different consequences*: the OpenAI key spends money per call, a Mapbox
 * token is metered per tile, and the key-free providers need nothing at all. Folding them into one
 * store would mean one "the key" concept in the UI covering three unrelated permissions.
 *
 * Same trade as the OpenAI key and the same caveat: `localStorage`, readable by anything running on
 * this page, appropriate for a local single-user tool and not for a public deployment. Restrict the
 * token by referrer at the provider — every one of them supports it, and it is the only real
 * protection a browser-held key can have.
 */

const STORAGE_PREFIX = 'landscape-glade.imagery-key.';

export class ImageryKeyStore {
  private readonly cache = new Map<string, string | null>();

  /** The key for a provider, or null when none is stored. */
  get(providerId: string): string | null {
    if (!this.cache.has(providerId)) {
      try {
        this.cache.set(providerId, globalThis.localStorage?.getItem(STORAGE_PREFIX + providerId) ?? null);
      } catch {
        this.cache.set(providerId, null);
      }
    }
    return this.cache.get(providerId) ?? null;
  }

  has(providerId: string): boolean {
    const key = this.get(providerId);
    return key !== null && key.length > 0;
  }

  set(providerId: string, key: string): void {
    const trimmed = key.trim();
    if (trimmed.length === 0) {
      this.clear(providerId);
      return;
    }
    this.cache.set(providerId, trimmed);
    try {
      globalThis.localStorage?.setItem(STORAGE_PREFIX + providerId, trimmed);
    } catch {
      // Non-fatal: the key holds for this session only.
    }
  }

  clear(providerId: string): void {
    this.cache.set(providerId, null);
    try {
      globalThis.localStorage?.removeItem(STORAGE_PREFIX + providerId);
    } catch {
      // Already gone from memory, which is what matters.
    }
  }
}
